/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { state } from '@/cart/lib/state';
import { fetchGraphQl } from '@/cart/api';
import { handleFetchError } from '@/cart/lib/fetch-error';
import { CartModel } from '@/cart/data/models';
import { transformCart } from '@/cart/data/transforms';
import { events } from '@adobe-commerce/event-bus';
import { publishCartUpdateEvents } from '@/cart/lib/acdl';
import { getPersistedCartData } from '@/cart/lib/persisted-data';
import { ADD_PRODUCTS_TO_CART_MUTATION } from '../addProductsToCart/graphql/AddProductsToCartMutation';
import { UPDATE_PRODUCTS_FROM_CART_MUTATION } from './graphql/UpdateProductsMutation';

type UpdateProductsFromCart = Array<{
  uid: string;
  quantity: number;
  sku?: string;
  parentSku?: string;
  optionsUIDs?: string[];
  enteredOptions?: { uid: string; value: string }[];
  giftOptions?: {
    gift_wrapping_id?: string | null;
    gift_message: {
      to: string;
      from: string;
      message: string;
    };
  };
}>;

type ItemError = {
  message: string;
  path?: string[];
  extensions?: {
    category: string;
  };
};

/**
 * Collects individual item errors from cart items response
 * @param cartItems - The cart items from the response
 * @param updatedItemUIDs - Array of UIDs of items that were updated
 * @returns Array of ItemError objects
 */
const collectCartItemErrors = (
  cartItems: Array<{
    uid: string;
    errors?: Array<{ message: string; code: string }>;
  }>,
  updatedItemUIDs: string[]
): ItemError[] => {
  const itemErrors: ItemError[] = [];

  cartItems
    .filter(
      (item) => item.errors && updatedItemUIDs.some((uid) => uid === item.uid)
    )
    .forEach((item) => {
      item.errors?.forEach((error) => {
        itemErrors.push({
          message: error.message,
          path: [item.uid],
          extensions: {
            category: error.code,
          },
        });
      });
    });

  return itemErrors;
};

/**
 * Creates the arrays of cart items to be sent to the graphql mutations
 *
 * @param items
 * @param currentCartItems
 * @returns {
 *  itemsToAdd: Array<{
 *    sku: string;
 *    parentSku: string;
 *    quantity: number;
 *    optionsUIDs: string[];
 *    enteredOptions: { uid: string; value: string }[];
 *  }>
 *  itemsToRemove: Array<string>
 *  itemsToUpdate: Array<{
 *    uid: string;
 *    quantity: number;
 *  }>
 * }
 */
const getCartItems = (
  items: UpdateProductsFromCart,
  currentCartItems: Array<CartModel['items'][number]>
) => {
  const itemsToAdd: Array<{
    sku?: string;
    parentSku?: string;
    quantity: number;
    optionsUIDs?: string[];
    enteredOptions?: { uid: string; value: string }[];
  }> = [];
  // Items to remove from the cart will be items that have optionUIDs and
  // their optionUIDs are not the same as their matching entry in the current cart item identified by the uid
  const itemsToRemove: Array<string> = [];
  const itemsToUpdate: Array<{
    uid: string;
    quantity: number;
    giftOptions?: {
      gift_wrapping_id?: string | null;
      gift_message: {
        to: string;
        from: string;
        message: string;
      };
    };
  }> = [];

  items.forEach((item) => {
    // Check if the item exists in the cart
    const currentItem = currentCartItems.find(
      (cartItem) => cartItem.uid === item.uid
    );

    if (currentItem) {
      if (item.optionsUIDs) {
        const currentItemOptions = Object.values(
          currentItem?.selectedOptionsUIDs ?? {}
        );

        const { sku, topLevelSku: parentSku } = currentItem;
        const { optionsUIDs, enteredOptions, quantity } = item;

        itemsToAdd.push({
          sku,
          parentSku,
          quantity,
          optionsUIDs,
          enteredOptions,
        });

        // If the item has optionUIDs and they are not the exact same as the current item, remove the item
        if (
          item.optionsUIDs.some(
            (optionUID) => !currentItemOptions.includes(optionUID)
          )
        ) {
          itemsToRemove.push(item.uid);
        }
      } else {
        itemsToUpdate.push({
          uid: item.uid,
          quantity: item.quantity,
          giftOptions: item.giftOptions,
        });
      }
    } else {
      throw Error(
        `Invalid Cart Item UID: No matching cart entry found for ${item.uid}`
      );
    }
  });

  return {
    itemsToAdd,
    itemsToRemove,
    itemsToUpdate,
  };
};

const DELETE_ITEM_QUANTITY = 0;

export const updateProductsFromCart = async (
  items: UpdateProductsFromCart
): Promise<CartModel | null> => {
  const cartId = state.cartId;
  const cart = getPersistedCartData();

  if (!cartId) {
    return Promise.reject(new Error('Cart ID is not set'));
  }

  if (!cart) {
    return Promise.reject(new Error('Cart is not set'));
  }

  const { itemsToAdd, itemsToRemove, itemsToUpdate } = getCartItems(
    items,
    cart.items
  );

  // Array of promises to be resolved
  let results: Array<Promise<CartModel | null>> = [];

  // If there are items with options and matching cart items, add the products to the cart and remove the old one.
  // Note: The SKU must be the incoming parent sku and not the current cart item sku.
  if (itemsToAdd.length > 0) {
    results.push(
      fetchGraphQl(ADD_PRODUCTS_TO_CART_MUTATION, {
        variables: {
          cartId,
          cartItems: itemsToAdd.map(
            ({
              parentSku: sku,
              quantity,
              optionsUIDs: selected_options,
              enteredOptions: entered_options,
            }) => ({
              sku,
              quantity,
              selected_options,
              entered_options,
            })
          ),
        },
      })
        .then(({ errors, data }) => {
          // Collect individual item errors from the response
          const updatedItemsErrors = collectCartItemErrors(
            data?.addProductsToCart?.cart?.itemsV2.items || [],
            items.map((item) => item.uid)
          );

          // handle errors
          const _errors = [
            ...(data?.addProductsToCart?.user_errors ?? []),
            ...(errors ?? []),
            ...updatedItemsErrors,
          ];

          if (_errors.length > 0) return handleFetchError(_errors);

          if (itemsToRemove.length > 0) {
            // remove the old item
            return updateProductQuantities(
              cartId,
              itemsToRemove.map((uid) => ({
                uid,
                quantity: DELETE_ITEM_QUANTITY, //Delete Item
              }))
            ).catch((error) => {
              return Promise.reject(
                new Error(`Failed to update products in cart: ${error}`)
              );
            });
          }
          return Promise.resolve(transformCart(data?.addProductsToCart?.cart));
        })
        .then((payload) => {
          // emit events
          events.emit('cart/updated', payload);
          events.emit('cart/data', payload);

          // Publish cart update events
          publishCartUpdateEvents(payload!, items, state.locale ?? 'en-US');

          return Promise.resolve(payload);
        })
        .catch((error) => {
          return Promise.reject(
            new Error(`Failed to add products to cart: ${error}`)
          );
        })
    );
  }

  if (itemsToUpdate.length > 0) {
    results.push(
      updateProductQuantities(cartId, itemsToUpdate).catch((error) => {
        return Promise.reject(new Error(error));
      })
    );
  }

  // Return the last promise in the results array
  return Promise.all(results).then((results) => results[results.length - 1]);
};

const updateProductQuantities = async (
  cartId: string,
  items: UpdateProductsFromCart
): Promise<CartModel | null> => {
  return fetchGraphQl(UPDATE_PRODUCTS_FROM_CART_MUTATION, {
    variables: {
      cartId,
      cartItems: items.map(({ uid, quantity, giftOptions }) => ({
        cart_item_uid: uid,
        quantity,
        ...giftOptions,
      })),
    },
  }).then(({ errors, data }) => {
    // Collect individual item errors from the response using the new function
    const updatedItemsErrors = collectCartItemErrors(
      data?.updateCartItems?.cart?.itemsV2.items || [],
      items.map((item) => item.uid)
    );

    // handle errors
    const _errors = [
      ...(data?.updateCartItems?.user_errors ?? []),
      ...(errors ?? []),
      ...updatedItemsErrors,
    ];

    // transform cart
    const payload =
      data?.updateCartItems && transformCart(data.updateCartItems.cart);

    // Emit data event
    if (payload) {
      events.emit('cart/data', payload);
    }

    if (_errors.length > 0) return handleFetchError(_errors);

    // No errors, emit updated event
    events.emit('cart/updated', payload);

    // emit product updated events
    if (payload) {
      const updatedItems = payload.items.filter(
        (item: CartModel['items'][number]) =>
          items.some((updateItem) => updateItem.uid === item.uid)
      );

      events.emit('cart/product/updated', updatedItems);
    }

    // data collection
    if (payload) {
      // Publish cart update events
      publishCartUpdateEvents(payload, items, state.locale ?? 'en-US');
    }

    return payload;
  });
};
